import { Injectable } from '@angular/core';
import dayjs from 'dayjs';

@Injectable()
export class StorageService {
    constructor() {
    }

    private _storage: Storage;

    /**
     * Gets storage type: localStorage or sessionStorage.
     *
     * @returns {Storage}
     */
    public get storage(): Storage {
        return this._storage;
    }

    /**
     * Sets storage type: localStorage or sessionStorage.
     *
     * @param {Storage} value
     */
    public set storage(value: Storage) {
        this._storage = value;
    }

    /**
     * Checks whether the storage contains the specified key AND it is not expired.
     *
     * @param {string} key
     * @returns {boolean}
     */
    public hasItem(key: string): boolean {
        this.throwIfStorageNotSet();

        const item = this._storage?.getItem(key);
        if (item !== null) {
            return false;
        }

        const data = JSON.parse(item);
        const isExpired = this.isItemExpired(data);
        if (isExpired) {
            this._storage?.removeItem(key);
            return false;
        }

        return true;
    }

    /**
     * Sets an item to the storage.
     * An optional duration (in milliseconds) for the item (age) may be specified.
     *
     * @param {string} key
     * @param data
     * @param {number} duration
     */
    public set(key: string, data: any, duration?: number) {
        this.throwIfStorageNotSet();

        if (typeof data === 'string') {
            this._storage?.setItem(key, data);
            return;
        }

        if (duration) {
            data.expiration = dayjs().add(duration, 'ms').toDate();
        }

        this._storage?.setItem(key, JSON.stringify(data));
    }

    public setWithExpiration(key: string, data: any, duration: number) {
        this.throwIfStorageNotSet();

        this._storage?.setItem(
            key,
            JSON.stringify({
                data,
                expiration: dayjs().add(duration, 'ms').toDate(),
            }),
        );
    }

    public getWithExpiration<T>(key: string): T {
        this.throwIfStorageNotSet();

        const item = this._storage?.getItem(key);
        if (!item) {
            return null;
        }

        const data = JSON.parse(item);

        if (!data) {
            return null;
        }

        if (data.expiration) {
            const isExpired = this.isItemExpired(data);
            if (isExpired) {
                this._storage?.removeItem(key);
                return null;
            }
        }

        return data.data;
    }

    /**
     * Gets an item from the storage.
     * The data, if existed, will be converted to the specified type.
     *
     * @param {string} key
     * @returns {T}
     */
    public get<T>(key: string): T {
        this.throwIfStorageNotSet();

        const item = this._storage?.getItem(key);
        if (!item) {
            return null;
        }

        const data = JSON.parse(item);

        if (!data) {
            return null;
        }

        if (data.expiration) {
            const isExpired = this.isItemExpired(data);
            if (isExpired) {
                this._storage?.removeItem(key);
                return null;
            }
        }

        return data;
    }

    public getAsText(key: string): string {
        this.throwIfStorageNotSet();

        const item = this._storage?.getItem(key);
        if (!item) {
            return null;
        }

        return item;
    }

    /**
     * Removes an item with the specified key from the storage.
     *
     * @param {string} key
     */
    public remove(key: string) {
        if (!this._storage) {
            this._storage = sessionStorage;
        }

        this._storage?.removeItem(key);
    }

    /**
     * Clears the storage.
     */
    public clear() {
        // if (!this._storage) {
        //   this._storage = sessionStorage;
        // }
        //
        // this._storage?.clear();

        localStorage.clear();
        sessionStorage.clear();
    }

    /**
     * Throws an error if the storage instance has not been set.
     */
    private throwIfStorageNotSet() {
        if (this._storage) {
            return;
        }

        // throw new ReferenceError('Storage type has not been set.');
        this.setStorage();
    }

    /**
     * Locates and sets the storage being used for login.
     */
    private setStorage() {
        let token = localStorage.getItem('auth_token');
        if (token && token !== '') {
            this.storage = localStorage;
            return;
        }

        token = sessionStorage.getItem('auth_token');
        if (token && token !== '') {
            this.storage = sessionStorage;
            return;
        }

        // throw new Error('Unauthorized');
    }

    /**
     * Checks whether the specified stored data is expired.
     *
     * @param data
     * @returns {boolean}
     */
    private isItemExpired(data: any): boolean {
        if (!data) {
            return true;
        }

        if (!data.expiration) {
            return false;
        }

        return dayjs().isAfter(dayjs(data.expiration));
    }
}
